/**
 * API-1424 — upstream image hosts the `/api/v1/image-proxy` route is allowed
 * to fetch.
 *
 * This is the security boundary against open-proxy abuse: the route rejects
 * any `?src=` whose host is not in this set with `400`, so the proxy can
 * never be turned into a generic SSRF/egress relay.
 *
 * This set MUST stay in sync with `PROXIED_IMAGE_HOSTS` in
 * `services/backend-api/src/utils/imageProxy.ts` — the backend producer that
 * decides which `/events/discover` image URLs get rewritten to the proxy.
 * Both lists are pinned by unit tests so drift is caught in review.
 *
 * Hosts: Eventbrite's image proxy (`img.evbuc.com` / `cdn.evbuc.com`, ~87% of
 * ingested event images) + Ticketmaster's DAM CDN (`s1.ticketm.net`) +
 * Ticketmaster seat-map static images (`mapsapi.tmol.io` — FRE-1073 /
 * `EventMeta.seatmapUrl` / `seatmap.staticUrl`).
 *
 * WEB-596: first-party hosts that `toDisplayImageUrl` serves directly for
 * `<img>` (`cdn.loci.city`, Vercel Blob) are NOT in this synced provider set —
 * discover must keep rendering them without a proxy wrap. They are allowed
 * separately via `isOwnDirectImageHost` / `isAllowedImageProxyUpstreamHost` so
 * the share poster `fetch()` can still stay same-origin under `connect-src
 * 'self'` without widening CSP.
 */
export const ALLOWED_UPSTREAM_IMAGE_HOSTS: ReadonlySet<string> = new Set([
  'img.evbuc.com',
  'cdn.evbuc.com',
  's1.ticketm.net',
  'mapsapi.tmol.io',
]);

/** Infrastructure we control — rendered directly in `img-src`, not in the
 *  synced provider allowlist above. */
const OWN_DIRECT_IMAGE_HOSTS: ReadonlySet<string> = new Set(['cdn.loci.city']);

/** Vercel Blob bucket suffix — durable editor/admin media (API-1512). */
const VERCEL_BLOB_HOST_SUFFIX = '.public.blob.vercel-storage.com';

/** True when `host` is a first-party image host served directly for `<img>`. */
export function isOwnDirectImageHost(host: string): boolean {
  return OWN_DIRECT_IMAGE_HOSTS.has(host) || host.endsWith(VERCEL_BLOB_HOST_SUFFIX);
}

/** True when `host` is an upstream the image proxy may fetch from. */
export function isAllowedUpstreamImageHost(host: string): boolean {
  return ALLOWED_UPSTREAM_IMAGE_HOSTS.has(host);
}

/**
 * WEB-596 — hosts `/api/v1/image-proxy` may fetch: synced providers plus
 * first-party direct hosts so share poster fetches can stay under `'self'`.
 */
export function isAllowedImageProxyUpstreamHost(host: string): boolean {
  return isAllowedUpstreamImageHost(host) || isOwnDirectImageHost(host);
}
